import { action, noop } from '@reatom/framework'
import i18next from 'i18next'

import { pipe, Result } from '@blade/std-lib'
import { walletsModelAtom } from '@root/entities/wallet'
import { deleteWalletRequestAction } from '@root/entities/wallet/atoms/wallet-requests'
import { refreshDeviceWalletsAction } from '@root/features/secure-storage/atoms'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { Error } from '@root/shared'
import { reatomRequest } from '@root/shared/lib'
import { showSnackbarAction } from '@root/shared/ui-kit'

import type { WalletId, WalletModel } from '@root/entities/wallet'

export const walletDeletedEvent = action<[{ walletId: WalletId }]>(
  noop,
  'walletDeletedEvent',
)

const processDeleteWalletAction = action(
  async (ctx, { walletId }: { walletId: WalletId }) => {
    const { secureStorageService } = ctx.get(dependenciesAtom)
    const result = await ctx.schedule(() =>
      deleteWalletRequestAction(ctx, { walletId }),
    )
    if (Result.isFailure(result)) {
      return result
    }
    const deviceResult = await ctx.schedule(() =>
      secureStorageService.removeWallet(walletId),
    )

    return pipe(
      deviceResult,
      Result.map(() => ({ walletId: result.success.walletId })),
      Result.mapError((err) => Error.UnknownError.fallbackToUnknown(err)),
    )
  },
  'processDeleteWalletAction',
)

export const reatomDeleteWallet = ({ wallet }: { wallet: WalletModel }) => {
  const deleteStateAtom = reatomRequest({
    // eslint-disable-next-line @reatom/unit-naming-rule
    name: `deleteStateAtom_${wallet.id}`,
    execAction: processDeleteWalletAction,
    prepare: (_) => {
      return { walletId: wallet.id }
    },
    onResult: (ctx, result) => {
      const walletName = ctx.get(wallet.name)

      if (Result.isFailure(result)) {
        showSnackbarAction(ctx, {
          text: i18next.t('wallet.delete_wallet.failure', { walletName }),
          intent: 'error',
        })
        return
      }

      const model = ctx.get(walletsModelAtom)
      model.delete(ctx, wallet.id)
      refreshDeviceWalletsAction(ctx)
      showSnackbarAction(ctx, {
        text: i18next.t('wallet.delete_wallet.success', {
          walletName,
        }),
        intent: 'success',
      })
      walletDeletedEvent(ctx, { walletId: wallet.id })
    },
  })

  return deleteStateAtom
}
